// @oasis-permission-inherits: /videos (mirage-videos:view)
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { Crown, Loader2, Pause, Play, RotateCcw } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { Button } from "~/components/ui/button";
import {
  clearMarks,
  isBest,
  isDefective,
  markState,
  markStateMessage,
  marksFromVerdicts,
  setBest,
  toggleDefective,
  toVerdictPayload,
  type DpoPanelMarks,
} from "~/modules/dpo-review/drafts";
import {
  permanentlySkipDpoReviewPanelMutation,
  requestDpoReviewSegmentsMutation,
  saveDpoReviewVerdictsMutation,
  skipDpoReviewPanelMutation,
  submitDpoReviewPanelMutation,
} from "~/modules/dpo-review/mutations";
import {
  blockedMessage,
  groupClipsIntoRounds,
  nextReviewablePanelId,
} from "~/modules/dpo-review/panels";
import { getDpoReviewPanelQuery, listDpoReviewPanelsQuery } from "~/modules/dpo-review/queries";
import { DPO_PLAYBACK_RATES, useSyncPlayback } from "~/modules/dpo-review/use-sync-playback";

import { DpoClipPlayerDialog } from "./-components/clip-player-dialog";
import { DpoClipTile, type DpoClipMark } from "./-components/clip-tile";
import { DpoCompareDialog, type DpoCompareSide } from "./-components/compare-dialog";
import { DpoPermanentSkipDialog } from "./-components/permanent-skip-dialog";

export const Route = createFileRoute("/_dash/videos/evals/dpo-review/$panelId")({
  component: RouteComponent,
});

const REFERENCE_KEY = "__reference";
const FLAGGED_KEY = "__flagged";

const AUTOSAVE_DELAY_MS = 600;

const TILE_SIZES = [
  { label: "L", value: 216 },
  { label: "XL", value: 290 },
  { label: "XXL", value: 380 },
];

function RouteComponent() {
  const { panelId } = Route.useParams();
  const navigate = useNavigate();
  const playback = useSyncPlayback();

  const panelQuery = getDpoReviewPanelQuery.useQuery({ pathParams: { panelId } });
  const panelsQuery = listDpoReviewPanelsQuery.useQuery({ params: {} }, { retry: false });
  const [marks, setMarks] = useState<DpoPanelMarks | null>(null);
  const [compareKeys, setCompareKeys] = useState<string[]>([]);
  const [bestArmed, setBestArmed] = useState(false);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [comparing, setComparing] = useState(false);
  const [tileSize, setTileSize] = useState(TILE_SIZES[0].value);
  const [isSaving, setIsSaving] = useState(false);
  const [showPermanentSkipConfirm, setShowPermanentSkipConfirm] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const panel = panelQuery.data?.panel;
  const reference = panelQuery.data?.reference;
  const flaggedRender = panelQuery.data?.flaggedRender;
  const clips = useMemo(() => panelQuery.data?.clips ?? [], [panelQuery.data]);
  const rounds = useMemo(() => groupClipsIntoRounds(clips), [clips]);
  const serverMarks = useMemo(
    () => marksFromVerdicts(panelQuery.data?.viewerVerdicts ?? []),
    [panelQuery.data]
  );
  const current = marks ?? serverMarks;
  const state = markState(current);
  const blocked = panel ? blockedMessage(panel) : null;
  let saveLabel = "";
  if (isSaving) {
    saveLabel = "saving";
  } else if (savedAt) {
    saveLabel = "saved";
  }
  const canJudge = Boolean(panel?.reviewable) && panel?.viewerStatus !== "submitted";

  const clipFor = useCallback(
    (seedKey: string) => clips.find((clip) => String(clip.seed) === seedKey),
    [clips]
  );

  const mutate = useCallback((next: DpoPanelMarks) => {
    setMarks(next);
    setBestArmed(false);
  }, []);

  const persist = useCallback(
    async (next: DpoPanelMarks) => {
      setIsSaving(true);
      try {
        await saveDpoReviewVerdictsMutation.trigger({
          pathParams: { panelId },
          params: { verdicts: toVerdictPayload(next) },
        });
        setSavedAt(Date.now());
      } catch (error) {
        toast.error("Failed to save marks", {
          description: error instanceof Error ? error.message : "Unknown error",
        });
      } finally {
        setIsSaving(false);
      }
    },
    [panelId]
  );

  useEffect(() => {
    if (!marks || !canJudge) {
      return;
    }
    const timer = window.setTimeout(() => void persist(marks), AUTOSAVE_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [canJudge, marks, persist]);

  const advance = useCallback(async () => {
    const { data } = await panelsQuery.refetch();
    const nextId = nextReviewablePanelId(data?.panels ?? [], panelId);
    if (nextId && nextId !== panelId) {
      await navigate({
        to: "/videos/evals/dpo-review/$panelId",
        params: { panelId: nextId },
      });
      return;
    }
    await panelQuery.refetch();
  }, [navigate, panelId, panelQuery, panelsQuery]);

  const submit = useCallback(async () => {
    setIsSaving(true);
    try {
      await saveDpoReviewVerdictsMutation.trigger({
        pathParams: { panelId },
        params: { verdicts: toVerdictPayload(current) },
      });
      const result = await submitDpoReviewPanelMutation.trigger({
        pathParams: { panelId },
        params: {},
      });
      toast.success(`Submitted ${result.submitted} verdicts`, {
        description: "This panel is closed for you and stays open for the other reviewers",
      });
      setMarks(null);
      await advance();
    } catch (error) {
      toast.error("Failed to submit", {
        description: error instanceof Error ? error.message : "Unknown error",
      });
    } finally {
      setIsSaving(false);
    }
  }, [advance, current, panelId]);

  const skipPermanently = useCallback(async () => {
    setIsSaving(true);
    try {
      await permanentlySkipDpoReviewPanelMutation.trigger({
        pathParams: { panelId },
        params: {},
      });
      toast.success("Panel skipped permanently", {
        description: "No votes were submitted and this panel is closed for you",
      });
      setShowPermanentSkipConfirm(false);
      setMarks(null);
      await advance();
    } catch (error) {
      toast.error("Failed to skip panel", {
        description: error instanceof Error ? error.message : "Unknown error",
      });
    } finally {
      setIsSaving(false);
    }
  }, [advance, panelId]);

  const skip = useCallback(async () => {
    setIsSaving(true);
    try {
      await saveDpoReviewVerdictsMutation.trigger({
        pathParams: { panelId },
        params: { verdicts: toVerdictPayload(current) },
      });
      await skipDpoReviewPanelMutation.trigger({ pathParams: { panelId }, params: {} });
      toast.info("Progress saved", { description: "Come back to this panel any time" });
      await advance();
    } catch (error) {
      toast.error("Failed to save progress", {
        description: error instanceof Error ? error.message : "Unknown error",
      });
    } finally {
      setIsSaving(false);
    }
  }, [advance, current, panelId]);

  const requestSegments = useCallback(async () => {
    setIsSaving(true);
    try {
      const result = await requestDpoReviewSegmentsMutation.trigger({
        pathParams: { panelId },
        params: {},
      });
      toast.info("More segments requested", {
        description: `This panel reopens at round ${result.targetRounds}`,
      });
      setMarks(null);
      await advance();
    } catch (error) {
      toast.error("Failed to request more segments", {
        description: error instanceof Error ? error.message : "Unknown error",
      });
    } finally {
      setIsSaving(false);
    }
  }, [advance, panelId]);

  const toggleCompare = useCallback((seedKey: string) => {
    setCompareKeys((keys) =>
      keys.includes(seedKey) ? keys.filter((key) => key !== seedKey) : [...keys, seedKey].slice(-2)
    );
  }, []);

  const mediaForKey = useCallback(
    (key: string): DpoCompareSide | undefined => {
      if (key === REFERENCE_KEY) {
        return reference
          ? {
              side: { kind: "reference" },
              label: "reference",
              sublabel: "source appearance",
              url: reference.url ?? undefined,
              kind: reference.kind === "image" ? "image" : "video",
              canBeBest: false,
            }
          : undefined;
      }
      if (key === FLAGGED_KEY) {
        return flaggedRender
          ? {
              side: { kind: "flagged" },
              label: "flagged",
              sublabel: "the render that was flagged",
              url: flaggedRender.url ?? undefined,
              kind: "video",
              canBeBest: false,
            }
          : undefined;
      }
      const clip = clipFor(key);
      return clip
        ? {
            side: { kind: "seed", seed: clip.seed },
            label: `+${clip.offset}`,
            sublabel: `seed ${clip.seed}`,
            url: clip.url ?? undefined,
            kind: "video",
            canBeBest: true,
          }
        : undefined;
    },
    [clipFor, flaggedRender, reference]
  );

  const compareSides = useMemo(
    () => compareKeys.map(mediaForKey).filter((side): side is DpoCompareSide => Boolean(side)),
    [compareKeys, mediaForKey]
  );

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.repeat || comparing || expandedKey || event.metaKey || event.ctrlKey || !canJudge) {
        return;
      }
      const key = event.key.toLowerCase();
      if (event.key === " ") {
        event.preventDefault();
        playback.toggle();
        return;
      }
      if (key === "b") {
        setBestArmed((armed) => !armed);
        return;
      }
      if (key === "r") {
        playback.restart();
        return;
      }
      if (key === "x") {
        mutate(clearMarks());
        return;
      }
      if (key === "c" && compareSides.length === 2) {
        setComparing(true);
        return;
      }
      const digit = Number.parseInt(event.key, 10);
      const clip = clips[digit - 1];
      if (digit >= 1 && digit <= 9 && clip) {
        mutate(
          bestArmed
            ? setBest(current, String(clip.seed))
            : toggleDefective(current, String(clip.seed))
        );
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [
    bestArmed,
    canJudge,
    clips,
    comparing,
    compareSides.length,
    current,
    expandedKey,
    mutate,
    playback,
  ]);

  if (panelQuery.isLoading) {
    return <div className="p-6 text-sm text-muted-foreground">Loading panel...</div>;
  }

  if (panelQuery.error || !panel) {
    return (
      <div className="m-6 rounded-md border border-destructive bg-destructive/10 p-4 text-sm text-destructive">
        <p className="font-medium">Failed to load panel</p>
        {panelQuery.error && (
          <pre className="mt-2 whitespace-pre-wrap text-xs">{panelQuery.error.message}</pre>
        )}
      </div>
    );
  }

  const expandedMedia = expandedKey ? mediaForKey(expandedKey) : undefined;

  return (
    <div className="space-y-4 pb-28">
      <div>
        <Button asChild variant="link" size="sm" className="h-auto p-0">
          <Link to="/videos/evals/dpo-review">Back to DPO Review</Link>
        </Button>
        <h1 className="mt-1 font-mono text-xl font-bold">{panel.segmentHash.slice(0, 12)}</h1>
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-xs text-muted-foreground">
          <span>checkpoint {panel.checkpointSlug}</span>
          <span>
            {panel.visibleClipCount} of {panel.minedSeedCount} mined · cap {panel.seedCap}
          </span>
          <span>{panel.submittedCount} reviewer(s) submitted</span>
          <span>
            {panel.videoId
              ? `video ${panel.videoId} · segment ${panel.segmentId}`
              : "video unknown — flagged before identity capture"}
          </span>
          {panel.videoId && (
            <Button asChild variant="link" size="sm" className="h-auto p-0 font-mono text-xs">
              <Link to="/videos/view/$videoId" params={{ videoId: panel.videoId }}>
                open in mirage videos
              </Link>
            </Button>
          )}
        </div>
        {panel.flagReason && (
          <p className="mt-2 text-sm text-muted-foreground">
            <q className="italic text-foreground">{panel.flagReason}</q>
            {panel.flaggedBy && <span className="ml-2 font-mono text-xs">— {panel.flaggedBy}</span>}
          </p>
        )}
      </div>

      {blocked && (
        <div className="rounded-md border border-amber-500/50 bg-amber-500/10 p-3 text-sm text-amber-600">
          {blocked}
        </div>
      )}

      {(reference || flaggedRender) && (
        <section className="grid gap-2">
          <div className="flex items-center gap-2">
            <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
              judge against
            </span>
            <span className="h-px flex-1 bg-border" />
          </div>
          <div
            className="grid gap-3"
            style={{
              gridTemplateColumns: `repeat(auto-fill, minmax(${tileSize}px, 1fr))`,
            }}
          >
            {reference && (
              <DpoClipTile
                clipKey={REFERENCE_KEY}
                label="reference"
                sublabel="source appearance"
                url={reference.url ?? undefined}
                kind={reference.kind === "image" ? "image" : "video"}
                tone="reference"
                mark="none"
                compareIndex={compareKeys.indexOf(REFERENCE_KEY)}
                isAudioSource={playback.audioKey === REFERENCE_KEY}
                isErrored={playback.errored.has(REFERENCE_KEY)}
                isPlaying={playback.playingKeys.has(REFERENCE_KEY)}
                synced={false}
                onCompare={toggleCompare}
                onExpand={setExpandedKey}
                onClaimAudio={playback.setAudioKey}
                onTogglePlay={playback.toggleOne}
                onPlayStateChange={playback.markPlaying}
                registerVideo={playback.registerVideo}
                onReady={playback.markReady}
                onError={playback.markErrored}
              />
            )}
            {flaggedRender && (
              <DpoClipTile
                clipKey={FLAGGED_KEY}
                label="flagged"
                sublabel="the render that was flagged"
                url={flaggedRender.url ?? undefined}
                kind="video"
                tone="flagged"
                mark="none"
                compareIndex={compareKeys.indexOf(FLAGGED_KEY)}
                isAudioSource={playback.audioKey === FLAGGED_KEY}
                isErrored={playback.errored.has(FLAGGED_KEY)}
                isPlaying={playback.playingKeys.has(FLAGGED_KEY)}
                synced={false}
                onCompare={toggleCompare}
                onExpand={setExpandedKey}
                onClaimAudio={playback.setAudioKey}
                onTogglePlay={playback.toggleOne}
                onPlayStateChange={playback.markPlaying}
                registerVideo={playback.registerVideo}
                onReady={playback.markReady}
                onError={playback.markErrored}
              />
            )}
          </div>
        </section>
      )}

      <div className="grid gap-6">
        {rounds.map((round) => (
          <section key={round.index} className="grid gap-2">
            <div className="flex items-center gap-2">
              <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                round {round.index + 1} — seeds +{round.clips[0]?.offset}…+
                {round.clips.at(-1)?.offset}
              </span>
              <span className="h-px flex-1 bg-border" />
            </div>
            <div
              className="grid gap-3"
              style={{
                gridTemplateColumns: `repeat(auto-fill, minmax(${tileSize}px, 1fr))`,
              }}
            >
              {round.clips.map((clip) => (
                <DpoClipTile
                  key={String(clip.seed)}
                  clipKey={String(clip.seed)}
                  label={`+${clip.offset}`}
                  sublabel={`seed ${clip.seed}`}
                  url={clip.url ?? undefined}
                  mark={markOf(current, String(clip.seed))}
                  compareIndex={compareKeys.indexOf(String(clip.seed))}
                  isAudioSource={playback.audioKey === String(clip.seed)}
                  isErrored={playback.errored.has(String(clip.seed))}
                  onToggleDefective={
                    canJudge ? (key) => mutate(toggleDefective(current, key)) : undefined
                  }
                  onMarkBest={canJudge ? (key) => mutate(setBest(current, key)) : undefined}
                  onCompare={toggleCompare}
                  onExpand={setExpandedKey}
                  onClaimAudio={playback.setAudioKey}
                  isPlaying={playback.playingKeys.has(String(clip.seed))}
                  onTogglePlay={playback.toggleOne}
                  onPlayStateChange={playback.markPlaying}
                  registerVideo={playback.registerVideo}
                  onReady={playback.markReady}
                  onError={playback.markErrored}
                />
              ))}
            </div>
          </section>
        ))}
      </div>

      <div className="fixed inset-x-0 bottom-0 z-20 flex flex-wrap items-center gap-2 border-t bg-background/95 px-4 py-2 backdrop-blur">
        <Button size="sm" onClick={playback.toggle}>
          {playback.playing ? <Pause className="size-4" /> : <Play className="size-4" />}
          {playbackLabel(playback.started, playback.playing)}
        </Button>
        <Button size="sm" variant="ghost" onClick={playback.restart}>
          <RotateCcw className="size-4" />
          Restart
        </Button>
        {DPO_PLAYBACK_RATES.map((rate) => (
          <Button
            key={rate}
            size="xs"
            variant={playback.rate === rate ? "default" : "outline"}
            className="font-mono"
            onClick={() => playback.setRate(rate)}
          >
            {rate}×
          </Button>
        ))}
        {TILE_SIZES.map((size) => (
          <Button
            key={size.value}
            size="xs"
            variant={tileSize === size.value ? "default" : "outline"}
            className="font-mono"
            onClick={() => setTileSize(size.value)}
          >
            {size.label}
          </Button>
        ))}
        {bestArmed && (
          <span className="inline-flex items-center gap-1 rounded-md bg-emerald-500/10 px-2 py-1 font-mono text-[11px] text-emerald-500">
            <Crown className="size-3" />
            press 1-9 to mark best
          </span>
        )}
        <span className="ml-auto font-mono text-[11px] text-muted-foreground">
          {markStateMessage(current)}
        </span>
        <Button
          size="sm"
          variant="outline"
          disabled={compareSides.length !== 2}
          onClick={() => setComparing(true)}
        >
          Compare {compareSides.length}/2
        </Button>
        <span className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground">
          {saveLabel}
        </span>
        <Button
          size="sm"
          variant="ghost"
          disabled={!canJudge || isSaving}
          onClick={() => void skip()}
        >
          Skip for now
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={!canJudge || isSaving}
          onClick={() => setShowPermanentSkipConfirm(true)}
        >
          Skip permanently
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={!canJudge || isSaving || panel.atSeedCap}
          title={panel.atSeedCap ? `At the ${panel.seedCap}-seed cap` : undefined}
          onClick={() => void requestSegments()}
        >
          Request more segments
        </Button>
        <Button
          size="sm"
          disabled={!canJudge || isSaving || state !== "ready"}
          onClick={() => void submit()}
        >
          {isSaving && <Loader2 className="size-4 animate-spin" />}
          Submit
        </Button>
      </div>

      <DpoPermanentSkipDialog
        open={showPermanentSkipConfirm}
        pending={isSaving}
        onOpenChange={setShowPermanentSkipConfirm}
        onConfirm={() => void skipPermanently()}
      />

      <DpoClipPlayerDialog
        open={Boolean(expandedKey)}
        title={expandedMedia?.label ?? ""}
        subtitle={expandedMedia?.sublabel ?? ""}
        url={expandedMedia?.url}
        kind={expandedMedia?.kind}
        rate={playback.rate}
        onRateChange={playback.setRate}
        onOpenChange={(open) => {
          if (!open) {
            setExpandedKey(null);
          }
        }}
      />

      <DpoCompareDialog
        open={comparing}
        left={compareSides[0]}
        right={compareSides[1]}
        rate={playback.rate}
        onRateChange={playback.setRate}
        onOpenChange={setComparing}
        onMarkBest={(side) => {
          if (side.kind === "seed") {
            const clip = clips.find((entry) => entry.seed === side.seed);
            if (clip) {
              mutate(setBest(current, String(clip.seed)));
            }
          }
        }}
        onToggleDefective={(side) => {
          if (side.kind === "seed") {
            const clip = clips.find((entry) => entry.seed === side.seed);
            if (clip) {
              mutate(toggleDefective(current, String(clip.seed)));
            }
          }
        }}
      />
    </div>
  );
}

function markOf(marks: DpoPanelMarks, seedKey: string): DpoClipMark {
  if (isBest(marks, seedKey)) {
    return "best";
  }
  return isDefective(marks, seedKey) ? "defective" : "none";
}

function playbackLabel(started: boolean, playing: boolean): string {
  if (!started) {
    return "Play all with audio";
  }
  return playing ? "Pause all" : "Play all";
}
